import type { CalendarEvent } from '../types.js';

/**
 * Visibility rule: an event shows in a group view if its primary group
 * matches OR it's been cross-posted to that group. This mirrors the DEMO
 * `isVisibleToGroup(e, groupId)` helper.
 *
 * `groupIds` is a multi-select set; an empty set (or `null`/`undefined`)
 * means "all groups" and the event always passes.
 */
export function isVisibleToGroups(
  event: CalendarEvent,
  groupIds: ReadonlySet<string> | null | undefined,
): boolean {
  if (groupIds === null || groupIds === undefined || groupIds.size === 0) {
    return true;
  }
  if (event.groupId !== null && event.groupId !== undefined && groupIds.has(event.groupId)) {
    return true;
  }
  if (event.crossPostedTo !== undefined) {
    for (const g of event.crossPostedTo) {
      if (groupIds.has(g)) return true;
    }
  }
  return false;
}

/** Compose all three filter facets (kind / group / status). All facets
 *  pass when their set is empty. */
export function applyCalendarFilters(
  events: readonly CalendarEvent[],
  filters: {
    readonly kinds?: ReadonlySet<string>;
    readonly groups?: ReadonlySet<string>;
    readonly statuses?: ReadonlySet<NonNullable<CalendarEvent['status']>>;
  },
): readonly CalendarEvent[] {
  const { kinds, groups, statuses } = filters;
  return events.filter((e) => {
    if (kinds !== undefined && kinds.size > 0 && !kinds.has(e.kind)) return false;
    if (groups !== undefined && groups.size > 0 && !isVisibleToGroups(e, groups)) return false;
    if (
      statuses !== undefined &&
      statuses.size > 0 &&
      (e.status === undefined || !statuses.has(e.status))
    )
      return false;
    return true;
  });
}
